import { ChevronDownIconXs, CloseIconSm, GlobeIconMd, GlobeIconSm, RestaurantIconMd, SearchIconSm } from '@chownow/pantry/icons';

import { Fragment, useState, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import PropTypes from 'prop-types';

import { breakpoints } from 'styles/theme.variables';

import { useDisclosure, useDebouncedValue, useMediaQuery } from '@mantine/hooks';
import { useCubohActionBar } from 'hooks/useCubohActionBar';
import { useGetLocationHomePath } from 'hooks/useGetLocationHomePath';
import { useAuth } from 'hooks/useAuth';

import { Button, Card, Divider, Indicator, Modal, Stack, Text, TextInput } from '@mantine/core';

import { PRICING_PLANS } from 'constants/chownow';
import { LOCATION_PATH } from 'constants/path';
import { getUpdatedLocationPath } from 'scopes/nav';

// Exclusively for Cuboh Only users
// but functions & styled the same as the "LocationSelector" component
export default function CubohLeftNavSelector({ toggleNav }) {
  const { setActionBarSingleSelectedOption, singleSelectedCompany } = useCubohActionBar();
  const getLocationHomePath = useGetLocationHomePath();

  const { companies } = useAuth();

  const locations = useMemo(() => Object.values(companies ?? {}), [companies]);
  const location = singleSelectedCompany;

  const [locationsModalOpened, locationsModalHandlers] = useDisclosure(false);
  const [searchQuery, setSearchQuery] = useState();
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 200);
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.maxMobile})`);

  const { pathname } = useLocation();
  const params = useParams();
  const navigate = useNavigate();

  const isHqLevelPage = !pathname.includes(LOCATION_PATH);

  const lowerDebouncedSearchQuery = debouncedSearchQuery?.toLowerCase();
  const filteredList = locations.filter(loc => {
    if (!lowerDebouncedSearchQuery) {
      return true;
    }

    const includesName = (loc.name ?? '').toLowerCase().includes(lowerDebouncedSearchQuery);
    const includesAddress = (loc.address ?? '').toLowerCase().includes(lowerDebouncedSearchQuery);
    return includesName || includesAddress;
  });

  async function handleLocationSelection(id) {
    locationsModalHandlers.close();

    if (id) {
      setActionBarSingleSelectedOption(JSON.stringify({ companyID: Number(id) }));

      if (isHqLevelPage) {
        navigate(getLocationHomePath(id));
      } else {
        navigate(
          getUpdatedLocationPath({
            pathname,
            id,
            prevId: params.restaurantId,
            isUserCubohOnly: true,
          }),
        );
      }
    } else {
      navigate('/');
    }
  }

  return (
    <>
      <Button
        onClick={() => {
          if (isMobile) toggleNav();
          locationsModalHandlers.open();
        }}
        variant="ghost-navigation"
        leftSection={isHqLevelPage ? <GlobeIconSm /> : <RestaurantIconMd />}
        rightSection={<ChevronDownIconXs />}
        fullWidth
        justify="space-between"
        className="locationSelector"
        styles={{
          root: {
            borderTop: isMobile ? '1px solid var(--border-secondary)' : 'none',
            marginTop: isMobile && 'var(--mantine-spacing-md)',
          },
          label: { flex: 1 },
        }}
      >
        <Text truncate="end">{isHqLevelPage ? 'Headquarters' : location?.name}</Text>
      </Button>
      <Modal
        opened={locationsModalOpened}
        onClose={locationsModalHandlers.close}
        title={
          <>
            <Text variant="caption-lg">Switch Restaurants</Text>
            <TextInput
              value={searchQuery}
              leftSection={<SearchIconSm />}
              placeholder="Search"
              rightSectionPointerEvents="all"
              rightSection={
                <CloseIconSm
                  aria-label="Clear input"
                  onClick={() => setSearchQuery('')}
                  style={{ display: searchQuery ? undefined : 'none' }}
                />
              }
              onChange={event => setSearchQuery(event.currentTarget.value)}
              size="md"
              mt="sm"
              mr="calc(var(--mantine-spacing-md) * -1)"
            />
          </>
        }
        size={isMobile ? 'sm' : 'lg'}
        styles={{ content: { height: '720px' }, title: { flex: 1 } }}
      >
        {filteredList?.length ? (
          <>
            <Button
              variant="menu-item"
              onClick={() => handleLocationSelection()}
              leftSection={!isMobile && <GlobeIconMd />}
              mb="xs"
              size={isMobile ? 'sm' : 'md'}
            >
              <Text variant="label-md">Headquarters</Text>
            </Button>
            {filteredList.map(loc => (
              <Fragment key={loc.id}>
                <Divider />
                <Button
                  variant="menu-item"
                  onClick={() => handleLocationSelection(loc.id)}
                  size={isMobile ? 'sm' : 'md'}
                  leftSection={!isMobile && <RestaurantIconMd />}
                  rightSection={<Indicator label={PRICING_PLANS.hub} variant="active" size="16" />}
                  my="xs"
                >
                  <Stack gap="0" style={{ maxWidth: '100%' }}>
                    <Text truncate="end" variant="label-md" mb="xs3" style={{ maxWidth: '100%' }}>
                      {loc.name}
                    </Text>
                    <Text variant="sublabel-sm" tt="capitalize">
                      {loc.address}
                    </Text>
                  </Stack>
                </Button>
              </Fragment>
            ))}
          </>
        ) : (
          <Card bg="var(--surface-secondary)" p="xl">
            <Text align="center" variant="body-md">
              No results
            </Text>
          </Card>
        )}
      </Modal>
    </>
  );
}

CubohLeftNavSelector.propTypes = {
  toggleNav: PropTypes.func,
};
